import React from "react";
import clsx from "clsx";
import Tooltip from "../Tooltip/Tooltip";
// Caso use Fluent UI icons, você pode importar o ícone ChevronRight
// import { ChevronRight20Regular } from "@fluentui/react-icons";

interface BreadcrumbsProps {
  items: Array<{ label: string; key: string; icon?: React.ReactNode }>;
  onClick?: (key: string) => void;
  theme?: "light" | "dark";
  className?: string;
  separator?: React.ReactNode; // permite customizar o separador
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  onClick,
  theme = "light",
  className,
  separator,
}) => {
  // Use o separador padrão ou Fluent UI
  const defaultSeparator = (
    // <ChevronRight20Regular className={clsx("mx-1", theme === "dark" ? "text-[#b3baff]" : "text-[#605e5c]")} aria-hidden="true" />
    <span
      className={clsx(
        "mx-1 select-none text-base",
        // Segue InputField: text-[#b3baff] no dark, text-[#605e5c] no light
        theme === "dark" ? "text-[#b3baff]" : "text-[#605e5c]"
      )}
      aria-hidden="true"
    >/</span>
  );

  const MAX_VISIBLE = 3; // Mostra até 3 itens (primeiro, penúltimo, último)

  function getVisibleItems(items: BreadcrumbsProps['items']) {
    if (items.length <= MAX_VISIBLE) return items.map(item => ({ ...item, show: true }));
    return [
      { ...items[0], show: true },
      { key: "__ellipsis__", label: "...", show: false },
      { ...items[items.length - 2], show: true },
      { ...items[items.length - 1], show: true },
    ];
  }

  const visibleItems = getVisibleItems(items);

  function BreadcrumbLabelWithTooltip({
    label,
    icon,
    showTooltip = true,
  }: { label: string; icon?: React.ReactNode; showTooltip?: boolean }) {
    const spanRef = React.useRef<HTMLSpanElement>(null);
    const [truncated, setTruncated] = React.useState(false);

    React.useEffect(() => {
      const el = spanRef.current;
      if (el) {
        setTruncated(el.scrollWidth > el.clientWidth);
      }
    }, [label]);

    const labelSpan = (
      <span ref={spanRef} className="truncate block max-w-[120px] flex items-center gap-1">
        {icon && <span className="shrink-0">{icon}</span>}
        <span className="truncate">{label}</span>
      </span>
    );

    if (!showTooltip) return labelSpan;
    return truncated ? (
      <Tooltip tooltip={label}>{labelSpan}</Tooltip>
    ) : (
      labelSpan
    );
  }

  return (
    <nav
      className={clsx(
        "w-full max-w-full mb-6 relative font-spaceGrotesk",
        "rounded-md px-3 py-2 transition-colors duration-150",
        "flex items-center shadow-sm",
        theme === "dark"
          ? "bg-[#23242a] text-[#e3eaff] border border-[#33384d]"
          : "bg-white text-gray-900 border border-[#c8c6c4]",
        className
      )}
      aria-label={items.length > 1 ? "Navegação de páginas" : "Breadcrumb"}
      aria-live="polite" // Adicionado para acessibilidade dinâmica
    >
      <ol
        className={clsx(
          "flex flex-wrap gap-1 items-center m-0 p-0 list-none overflow-x-auto max-w-full",
          "scrollbar-thin scrollbar-thumb-[#e3eaff] scrollbar-track-transparent",
          "scrollbar-thumb-rounded-full"
          // "scrollbar-hide" // Removido se não houver plugin
        )}
      >
        {visibleItems.map((item, idx) =>
          item.key === "__ellipsis__" ? (
            <li key="ellipsis" className="flex items-center" aria-hidden="true">
              <span className="mx-1 text-[#605e5c] select-none">...</span>
            </li>
          ) : (
            <li key={item.key} className="flex items-center min-w-0">
              {idx > 0 && (separator ?? defaultSeparator)}
              <button
                type="button"
                className={clsx(
                  "outline-none", // Garante que nunca terá duplo outline
                  "transition-colors transition-shadow duration-150 font-spaceGrotesk px-1 rounded focus-visible:outline-2 focus-visible:outline-[#7b5cff] focus-visible:outline-offset-2 truncate max-w-[120px]",
                  "hover:shadow-[0_1px_6px_0_rgba(123,92,255,0.10)] focus:shadow-[0_2px_10px_0_rgba(123,92,255,0.18)]",
                  idx === visibleItems.length - 1
                    ? theme === "dark"
                      ? "font-semibold text-[#b3baff] cursor-default"
                      : "font-semibold text-[#0078d4] cursor-default"
                    : theme === "dark"
                      ? "hover:bg-[#232c3a] hover:text-[#7b5cff] text-[#e3eaff]"
                      : "hover:bg-[#f3f9fd] hover:text-[#0078d4] text-[#605e5c]"
                )}
                onClick={() => onClick?.(item.key)}
                disabled={idx === visibleItems.length - 1}
                aria-current={idx === visibleItems.length - 1 ? "page" : undefined}
                aria-label={item.label}
                tabIndex={idx === visibleItems.length - 1 ? -1 : 0}
                title={item.label}
              >
                <BreadcrumbLabelWithTooltip
                  label={item.label}
                  icon={item.icon}
                  showTooltip={idx !== visibleItems.length - 1}
                />
              </button>
            </li>
          )
        )}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;